import {
	Button,
	LinearProgress,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import "./FindTable.css";
import {
	LoginContext,
	LoginContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import { Availabilities } from "../../../types/entities";
import { ReservationApi } from "../../../api/reservationApi";
import { LangContext } from "../../../types/internationalization";
import { theme } from "../../../styles/themeProvider";
import { FindTableButton } from "../../buttons/FindTableButton";
import { DinersSelect } from "./DinersSelect";
import { mapSchedules } from "../../../utils/generateSchedules";
import { DateSelect } from "./DateSelect";
import { TimeSelect } from "./TimeSelect";
import { formatTime } from "../../../utils/dateFormat";
import { FormattedMessage, useIntl } from "react-intl";
import { ContactNumber } from "../../styled/ContactNumber";

// This component contains the selectors for: number of people, date and time.

// I have to send to the DateSelect component the disableDate function
// that filters according to the availability of the service.

// To the TimeSelect component, I send all the available schedules of the service
// I also generate schedules from the first schedule to the last one in ranges of 30 minutes

const FindTable = () => {
	const { formatMessage } = useIntl();
	const [loadingData, setLoadingData] = useState(true);
	const [loading, setLoading] = useState(false);
	const langContext = useContext(LangContext);
	const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

	const {
		activeStep,
		reservation,
		onChangeNumberDiners,
		onChangeDate,
		onChangeTime,
		onNext,
		onChangeStep,
	} = useContext(MultistepContext) as MultistepContextValues;

	const { isLogged, setIsCalledFromProfile } = useContext(
		LoginContext
	) as LoginContextValues;

	const [schedulesFound, setSchedulesFound] = useState<null | Availabilities>(
		null
	);
	const [mappedSchedules, setMappedSchedules] = useState<string[]>([]);
	const [disabled, setDisabled] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	// Everytime the reservation time changes, we have to generate different schedules for the corresponding day
	useEffect(() => {
		if (reservation.time !== null)
			setMappedSchedules(mapSchedules(reservation.service!, reservation.time));

		if (reservation.time !== null && reservation.schedule)
			setLoadingData(false);
	}, [reservation.time, reservation.schedule]);

	//Everytime the reservation time, diners or date changes, we have to clean the schedules found
	useEffect(() => {
		setSchedulesFound(null);
	}, [onChangeNumberDiners, onChangeDate, onChangeTime]);

	//When the user clicks on 'Find table' button, we have to call the api to get the schedules availables
	const handleFindTable = async () => {
		try {
			setLoading(true);
			setSchedulesFound(null);

			const reservationApi = new ReservationApi();
			const response = await reservationApi.findTable(
				reservation,
				reservation.service!.id,
				mappedSchedules
			);

			setTimeout(() => {
				setSchedulesFound(response.data);
				setLoading(false);
			}, 1000);

			setError(null);
		} catch (e) {
			setError(e.message);
		}
	};

	const handleError = (error: string, disable?: boolean) => {
		setError(error);
		if (disable) setDisabled(true);
	};

	return (
		<Stack direction="column" spacing={2} width="100%">
			<Stack
				sx={{
					width: { mobile: "106%", xs: "100%" },
					alignSelf: "center",
					display: loadingData ? "none" : "flex",
				}}
			>
				<Stack direction="row">
					<DinersSelect disabled={disabled} />
					<DateSelect disabled={disabled} handleError={handleError} />
					<TimeSelect disabled={disabled} schedules={mappedSchedules} />
					{!isSmall && (
						<FindTableButton
							disabled={disabled}
							handleFindTable={handleFindTable}
							styles={styles.cornerButton}
						/>
					)}
				</Stack>
				{isSmall && (
					<FindTableButton
						disabled={disabled}
						handleFindTable={handleFindTable}
						styles={styles.lowerButton}
					/>
				)}
			</Stack>
			{(loading || loadingData) && <LinearProgress />}
			{schedulesFound && (
				<Stack
					direction={isSmall ? "column" : "row"}
					spacing={3}
					width="100%"
					paddingTop={2}
				>
					{Object.entries(schedulesFound.availabilities).map(
						([key, value], index) => (
							<Button
								key={index}
								variant="contained"
								fullWidth
								disabled={!value}
								onClick={() => {
									onChangeTime(key);
									setIsCalledFromProfile(false);
									if (isLogged) onChangeStep(activeStep + 2);
									else onNext();
								}}
							>
								{formatTime(key, langContext?.locale!)}
							</Button>
						)
					)}
				</Stack>
			)}

			{schedulesFound &&
				!schedulesFound.availabilities[reservation.schedule!.concat(":00")] && (
					<Typography fontWeight={400} color="error">
						{formatMessage({
							id: "findTable.notAvailableSchedule",
							defaultMessage:
								"No hay mesas disponibles para el horario seleccionado.",
						})}
					</Typography>
				)}
			{error && (
				<Typography sx={{ fontWeight: 400, color: theme.palette.error.main }}>
					{error}
				</Typography>
			)}
			<Typography fontWeight={400} alignSelf="flex-start" paddingTop={3}>
				<FormattedMessage
					id="findTable.moreDinersMessage"
					defaultMessage="Si desea reservar para más de 10 personas, por favor comunicarse al"
				/>{" "}
				<ContactNumber />
			</Typography>
		</Stack>
	);
};

export const styles = {
	cornerSelect: {
		flex: 1,
		borderRadius: "10px",
		borderTopRightRadius: "0px",
		borderBottomRightRadius: "0px",
		paddingLeft: "20px",
	},
	cornerSelectMobile: {
		flex: 1,
		borderRadius: "0px",
		borderTopLeftRadius: "10px",
		paddingLeft: { mobile: "2px", xs: "8px" },
	},
	mediumSelect: {
		flex: 1,
		borderRadius: "0px",
		paddingLeft: "20px",
	},
	mediumSelectMobile: {
		flex: 1,
		borderRadius: "0px",
		borderTopRightRadius: "10px",
		paddingLeft: { mobile: "2px", xs: "8px" },
	},
	datePicker: {
		flex: 1,
		borderRadius: "0px",
		width: "100%",
		minWidth: { sm: "180px", md: "190px", lg: "194px", xl: "200px" },
	},
	datePickerMobile: {
		flex: 1,
		minWidth: "99px",
		borderRadius: "0px",
	},
	cornerButton: {
		borderRadius: "10px",
		borderTopLeftRadius: "0px",
		borderBottomLeftRadius: "0px",
		paddingLeft: "12px",
		flex: 1,
	},
	lowerButton: {
		borderRadius: "10px",
		borderTopRightRadius: "0px",
		borderTopLeftRadius: "0px",
		height: "50px",
	},
};

export default FindTable;
